import React from "react";
import * as ReactRouter from "react-router";
import { Switch, Route as UnwrappedRoute } from "react-router-dom";

import type { AppUrlOpen, AppState as CapAppState } from "@capacitor/app";
import { App as CapApp } from "@capacitor/app";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/react";
import type { History } from "history";
import { noop } from "lodash";

import { ViewportContextProvider } from "~/common/ViewportContext";
import FullPageError, { FullPageOffline, FullPageSessionExpired } from "~/error/FullPageError";
import { CompanionModeTitleBar } from "~/layout/TitleBar/CompanionModeTitleBar";
import TitleBarProvider from "~/layout/TitleBar/TitleBarProvider";
import growthbook from "~/lib/ab/growthbook";
import { AuthProvider } from "~/lib/auth/index";
import { ensureAppUpdated } from "~/lib/ensureAppUpdated";
import history from "~/lib/history";
import { configureIntune } from "~/lib/intune/intune";
import { retryDynamicImport } from "~/lib/retryDynamicImport";
import { inCompanionMode, isNative } from "~/lib/utils/platform";
import { isExpectedNetworkError, isSessionExpiredError, NetworkError } from "~/types/errors";
import Lowda, { StaticLowda } from "~/ui/loading/Lowda";
import { StylesheetsProvider } from "~/uxSimplificationFeature/StylesheetsProvider";

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
const Route = Sentry.withSentryRouting(UnwrappedRoute);
const RouterContext = ReactRouter.__RouterContext; // eslint-disable-line no-underscore-dangle
const HistoryContext = (ReactRouter as typeof ReactRouter & Record<"__HistoryContext", React.Context<History>>)[
	"__HistoryContext"
];

function computeRootMatch(pathname: string) {
	return { path: "/", url: "/", params: {}, isExact: pathname === "/" };
}

function HistoryRouter({
	history,
	children,
}: React.PropsWithChildren<{
	history: History;
}>) {
	const location = React.useSyncExternalStore(
		React.useCallback(onStoreChange => history.listen(onStoreChange), [history]),
		React.useCallback(() => history.location, [history]),
		React.useCallback(() => history.location, [history]),
	);

	const routerContext = React.useMemo(
		() => ({
			history,
			location,
			match: computeRootMatch(location.pathname),
			staticContext: undefined,
		}),
		[history, location],
	);

	return (
		<RouterContext.Provider value={routerContext}>
			<HistoryContext.Provider value={history}>{children}</HistoryContext.Provider>
		</RouterContext.Provider>
	);
}

interface ErrorBoundaryState {
	error: Error | null;
}

class ErrorBoundary extends React.Component<{ children?: React.ReactNode }, ErrorBoundaryState> {
	state: ErrorBoundaryState = { error: null };

	retry = () => {
		this.setState({ error: null });
	};

	componentDidCatch(error: Error) {
		console.error("Router caught error:", error);

		if (window && window.SENTRY_DSN_PUBLIC && !isExpectedNetworkError(error)) {
			Sentry.captureException(error);
		}

		return this.setState({ error });
	}

	render() {
		if (this.state.error !== null) {
			if (this.state.error instanceof NetworkError) {
				return <FullPageOffline retry={this.retry} />;
			}
			if (isSessionExpiredError(this.state.error)) {
				return <FullPageSessionExpired />;
			}
			return <FullPageError message="Oops — Fellow failed to load" />;
		}

		return this.props.children;
	}
}

async function appListeners(history: History) {
	const listeners = await Promise.all([
		CapApp.addListener("appStateChange", (state: CapAppState) => {
			console.debug("App state changed. Is active?", state.isActive);
		}),

		CapApp.addListener("appUrlOpen", (data: AppUrlOpen) => {
			if (data.url.startsWith("msal")) {
				// these links are microsoft authentication links, they're handled separately by the capacitor-oauth2 plugin.
				return;
			}
			const url = new URL(data.url);

			let destination = url.pathname + url.search + url.hash;

			// Login tickets require a full browser navigation to work, a history push isn't sufficient for the authentication
			if (window.location.host !== url.host || url.pathname.startsWith("/auth/login/ticket")) {
				window.location.href = url.href;
				return;
			}

			if (url.pathname.startsWith("/auth/login")) {
				const next = url.searchParams.get("next");
				if (next != null) {
					destination = decodeURIComponent(next);
				}
			}

			history.push(destination);
		}),
	]);

	return () => {
		for (const listener of listeners) {
			void listener.remove();
		}
	};
}

function onServiceWorkerMessage(event: MessageEvent) {
	if (typeof event.data !== "object" || event.data === null) {
		return;
	}

	const { action } = event.data;

	if (action === "delegationStatusChange") {
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	}
}

function serviceWorkerListeners() {
	const sw = window.navigator.serviceWorker;
	if (sw) {
		sw.addEventListener("message", onServiceWorkerMessage);
		return () => sw.removeEventListener("message", onServiceWorkerMessage);
	}

	return noop;
}

// The importer passed to retryDynamicImport must be a bare import(); compose
// the update/Intune wrappers around its promise instead (see its docstring).
const Email = React.lazy(() => configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/email")))));
const Auth = React.lazy(() => ensureAppUpdated(retryDynamicImport(() => import("~/auth"))));
const Onboarding = React.lazy(() =>
	configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/onboarding")))),
);
const Public = React.lazy(() => configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/public")))));
const Apps = React.lazy(() => configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/integrations")))));
const App = React.lazy(() => configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/App")))));
const GuestMode = React.lazy(() =>
	configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/guestMode/GuestMode")))),
);
const CompanionMode = React.lazy(() =>
	configureIntune(ensureAppUpdated(retryDynamicImport(() => import("~/companionMode/CompanionMode")))),
);

if ("serviceWorker" in navigator && navigator.serviceWorker.register) {
	window.addEventListener("load", function () {
		if (!window.Cypress) {
			navigator.serviceWorker
				.register("/serviceWorkerRoot.js", {})
				.catch(e => console.warn("SW registration failed:", e));
		}
	});
}

export const Root = () => {
	React.useEffect(() => {
		let cleanupAppListeners: (() => void) | undefined;
		let cancelled = false;

		if (isNative) {
			void appListeners(history).then(cleanup => {
				if (cancelled) {
					cleanup();
					return;
				}

				cleanupAppListeners = cleanup;
			});
		}

		const cleanupServiceWorkerListeners = serviceWorkerListeners();

		return () => {
			cancelled = true;
			cleanupAppListeners?.();
			cleanupServiceWorkerListeners();
		};
	}, []);

	return (
		<AuthProvider>
			<ViewportContextProvider>
				<GrowthBookProvider growthbook={growthbook}>
					<StylesheetsProvider>
						<HistoryRouter history={history}>
							<TitleBarProvider>
								{/* Adding this here from the CompanionMode component */}
								{/* We need this to be available regardless of the new UX flag */}
								{/* Once the new UX is fully deployed, we can remove this */}
								{/* And move it back to the CompanionMode component */}
								{inCompanionMode && <CompanionModeTitleBar />}
								<ErrorBoundary>
									<React.Suspense fallback={<StaticLowda />}>
										<Switch>
											<Route path="/emails/" component={Email} />
											<Route path="/auth/" component={Auth} />
											<Route path="/o/" component={Onboarding} />
											<Route path="/public/" component={Public} />
											<Route path="/apps/" component={Apps} />
											<Route path="/guest/" component={GuestMode} />
											<Route path="/companion-mode/" component={CompanionMode} />
											<Route path="/" component={App} />
										</Switch>
									</React.Suspense>
									<Lowda />
								</ErrorBoundary>
							</TitleBarProvider>
						</HistoryRouter>
					</StylesheetsProvider>
				</GrowthBookProvider>
			</ViewportContextProvider>
		</AuthProvider>
	);
};
